import React, { useEffect, useMemo, useState } from "react";
import useAuth from "../../../Hooks/useAuth";
import { useAlert } from "../../../context/AlertContext";
import {
  useGetDealerDeviceQuotaOptionsMutation,
  useGiveDealerDeviceQuotaMutation,
  useTakeBackDealerDeviceQuotaMutation,
} from "../../../store/storageRoles/devicesApi";
import { getDeviceDisplayName } from "../../../utils/deviceUtils";
import DeviceQuotaDrawer from "../../Firm/Tabs/DeviceQuotaDrawer";

/**
 * Merkezden bayi cihazına Kota Ver / Kota Al (#335, sözleşme
 * dealer-device-moves.md v1.2) — bayideki cihazda Kota Dağıt'ın yerine.
 *
 * Bayi panelindeki cihaz kota işleminin kendisi: canlı (cihaz çevrimdışıysa
 * sunucu reddeder), bayi bakiyesi ↔ cihaz, fatura yok. Bakiye ve cihaz
 * kotası açılışta dealerDeviceQuotaOptions'tan gelir. Ana Cihazlar listesi ve
 * firma Cihazlar sekmesi kullanır.
 */
function CenterDealerQuotaDrawer({ open, device, direction, onClose, onDone }) {
  const { auth } = useAuth();
  const token = auth?.accessToken;
  const { showSuccess, showError } = useAlert();
  const [options, setOptions] = useState(null);

  const [getOptions] = useGetDealerDeviceQuotaOptionsMutation();
  const [give, giveStatus] = useGiveDealerDeviceQuotaMutation();
  const [takeBack, takeBackStatus] = useTakeBackDealerDeviceQuotaMutation();
  const status = direction === "take" ? takeBackStatus : giveStatus;

  useEffect(() => {
    if (!open || !device?._id) return;
    let cancelled = false;
    setOptions(null);
    giveStatus.reset();
    takeBackStatus.reset();
    getOptions({ token, id: device._id })
      .unwrap()
      .then((res) => {
        if (!cancelled) setOptions(res?.data || null);
      })
      .catch((err) => {
        if (cancelled) return;
        showError(err?.data?.message || "Bayi bakiyesi alınamadı.");
        onClose();
      });
    return () => {
      cancelled = true;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, device?._id]);

  useEffect(() => {
    if (!status.isSuccess && !status.isError) return;
    const name = `"${getDeviceDisplayName(device)}" (${device?.serialNo})`;
    if (status.isSuccess) {
      showSuccess(`${name} ${direction === "take" ? "cihazından kota alındı." : "cihazına kota verildi."}`);
      onClose();
      if (onDone) onDone();
    } else {
      showError(`${name} - ${status.error?.data?.message || "Kota işlemi başarısız."}`);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status.isSuccess, status.isError]);

  // Pencere ürünleri ve cihaz kotasını cihazdan okur — sunucunun güncel
  // değerleriyle besle; bakiye bayinin bakiyesi.
  const drawerDevice = useMemo(() => {
    if (!device) return device;
    const products = options?.products || [];
    return {
      ...device,
      productInfo: {
        productInfo: products.map((p) => ({
          productName: p.productName,
          quota: p.deviceQuota,
          quotaMax: p.quotaMax,
        })),
      },
    };
  }, [device, options]);

  const firmProducts = useMemo(
    () => (options?.products || []).map((p) => ({ productName: p.productName, quota: p.firmBalance })),
    [options]
  );

  const handleSubmit = ({ productName, quota, note }) => {
    const payload = { token, deviceId: device._id, productName, quota, note: note || "" };
    if (direction === "take") takeBack(payload);
    else give(payload);
  };

  return (
    <DeviceQuotaDrawer
      open={open}
      onClose={onClose}
      onSubmit={handleSubmit}
      direction={direction}
      device={drawerDevice}
      firmProducts={firmProducts}
      balanceLabel={options?.firm?.firmName ? `${options.firm.firmName} bakiyesi` : "Bayi bakiyesi"}
      isLoading={status.isLoading || (open && !options)}
    />
  );
}

export default CenterDealerQuotaDrawer;
